// src/components/Analytics/MetersGainedExplorer.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Box, Paper, Stack, Typography, Checkbox, FormControlLabel } from "@mui/material";
import { SimpleSelectOption, useActionOptions } from "../simple-select/simpleSelectOptions";
import { SingleSelect } from "../simple-select/SimpleSelect";
import { MetricEvent } from "../../requests/metricEvents";
import { EChartsMetersGainedScatter, ScatterChartPoint } from "./EChartsMetersGainedScatter";
import { HeatmapSmoothingOptions, buildSmoothedHeatmapPoints } from "./HeatMapSmoothing";
import { useActionTypes } from "../../requests/actionTypes";
import { DatabaseAction } from "../../business-logic/ActionBehavior/Action";
import { borderRadius, shadows } from "../../utils/designSystem";

type Props = {
  metricEvents?: MetricEvent[];
  selectedMatchId?: number;
};

const METRIC_OPTION_IDS = {
  metersGained: "metersGained",
  anyOccurrence: "anyOccurrence",
  setPieceResult: "setPieceResult",
} as const;

const metricOptions: SimpleSelectOption[] = [
  { id: METRIC_OPTION_IDS.metersGained, value: "Meters Gained" },
  { id: METRIC_OPTION_IDS.anyOccurrence, value: "Any Occurrence" },
  { id: METRIC_OPTION_IDS.setPieceResult, value: "Set Piece Result" },
];

const RADIO_BUTTON_CHECKED_SYMBOL =
  "path://M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z";
const CLOSE_SYMBOL =
  "path://M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z";

const VISUALIZATION_MODES = {
  scatter: "scatter",
  heatmap: "heatmap",
} as const;

const visualizationOptions: SimpleSelectOption[] = [
  { id: VISUALIZATION_MODES.scatter, value: "Scatter" },
  { id: VISUALIZATION_MODES.heatmap, value: "Heatmap" },
];

const territoryActions = [
  DatabaseAction.phase,
  DatabaseAction.openPlayKick,
  DatabaseAction.kickoff,
  DatabaseAction.maulStart,
  DatabaseAction.kickRestart,
  DatabaseAction.tapRestart,
  DatabaseAction.receiveKick,
];

export const PitchExplorer: React.FC<Props> = ({ metricEvents, selectedMatchId }) => {
  const actionOptions = useActionOptions() ?? [];
  const actionTypes = useActionTypes();

  const [selectedActionId, setSelectedActionId] = useState<string>(DatabaseAction.phase.toString());
  const [selectedMetricId, setSelectedMetricId] = useState<string>(METRIC_OPTION_IDS.metersGained);
  const [selectedVisualizationMode, setSelectedVisualizationMode] = useState<string>(
    VISUALIZATION_MODES.scatter
  );
  const [showHomeTeam, setShowHomeTeam] = useState(true);
  const [showAwayTeam, setShowAwayTeam] = useState(true);

  const territoryActionNames = useMemo(
    () => territoryActions.map((action) => actionTypes[action]),
    [actionTypes]
  );

  const isMetersGainedMetric = selectedMetricId === METRIC_OPTION_IDS.metersGained;
  const isSetPieceResultMetric = selectedMetricId === METRIC_OPTION_IDS.setPieceResult;

  const eventsForMatch = useMemo(() => {
    if (!metricEvents) {
      return [] as MetricEvent[];
    }

    return selectedMatchId
      ? metricEvents.filter((event) => event.match_id === selectedMatchId)
      : metricEvents;
  }, [metricEvents, selectedMatchId]);

  const metersGainedActions = useMemo(() => {
    return new Set(
      eventsForMatch.filter((event) => event.meters_gained !== 0).map((event) => event.action)
    );
  }, [eventsForMatch]);

  const occurrenceActions = useMemo(() => {
    return new Set(eventsForMatch.map((event) => event.action));
  }, [eventsForMatch]);

  const setPieceActions = useMemo(() => {
    return new Set(
      eventsForMatch.filter((event) => event.set_piece === 1).map((event) => event.action)
    );
  }, [eventsForMatch]);

  const availableActionOptions = useMemo(() => {
    if (isMetersGainedMetric) {
      return actionOptions.filter(
        (actionOption) =>
          metersGainedActions.has(actionOption.value) &&
          territoryActionNames.includes(actionOption.value)
      );
    }

    if (isSetPieceResultMetric) {
      return actionOptions.filter((actionOption) => setPieceActions.has(actionOption.value));
    }

    return actionOptions.filter((actionOption) => occurrenceActions.has(actionOption.value));
  }, [
    actionOptions,
    isMetersGainedMetric,
    isSetPieceResultMetric,
    metersGainedActions,
    occurrenceActions,
    setPieceActions,
    territoryActionNames,
  ]);

  useEffect(() => {
    if (!availableActionOptions.length) {
      if (selectedActionId !== "") {
        setSelectedActionId("");
      }
      return;
    }

    const hasSelection = availableActionOptions.some((option) => option.id === selectedActionId);
    if (!hasSelection) {
      const defaultOptionId =
        availableActionOptions.find((option) => option.id === DatabaseAction.phase.toString())
          ?.id ?? availableActionOptions[0].id;
      setSelectedActionId(defaultOptionId);
    }
  }, [availableActionOptions, selectedActionId]);

  const selectedActionOption = availableActionOptions.find(
    (option) => option.id === selectedActionId
  );

  const filteredPlotEvents = useMemo(() => {
    const allEvents = metricEvents ?? [];

    let filtered = selectedMatchId
      ? allEvents.filter((event) => event.match_id === selectedMatchId)
      : allEvents;

    if (selectedActionOption) {
      filtered = filtered.filter((event) => event.action === selectedActionOption.value);
    }

    if (isSetPieceResultMetric) {
      filtered = filtered.filter((event) => event.set_piece === 1);
    }

    const homeTeamName = eventsForMatch[0]?.home_team;
    const awayTeamName = eventsForMatch[0]?.away_team;

    if (!showHomeTeam && homeTeamName) {
      filtered = filtered.filter((event) => event.team !== homeTeamName);
    }
    if (!showAwayTeam && awayTeamName) {
      filtered = filtered.filter((event) => event.team !== awayTeamName);
    }

    return filtered;
  }, [
    eventsForMatch,
    isSetPieceResultMetric,
    metricEvents,
    selectedActionOption,
    selectedMatchId,
    showAwayTeam,
    showHomeTeam,
  ]);

  const allowedActionNames = useMemo(() => {
    return isMetersGainedMetric
      ? new Set(availableActionOptions.map((option) => option.value))
      : undefined;
  }, [availableActionOptions, isMetersGainedMetric]);

  const scatterEvents = useMemo(() => {
    return filteredPlotEvents
      .filter((event) => !allowedActionNames || allowedActionNames.has(event.action))
      .filter((event) => event.possession === 1);
  }, [allowedActionNames, filteredPlotEvents]);

  const smoothingOptions = useMemo<HeatmapSmoothingOptions>(
    () => ({
      metricMode: isMetersGainedMetric ? "meters" : "occurrence",
    }),
    [isMetersGainedMetric]
  );

  const heatmapPoints = useMemo(
    () => buildSmoothedHeatmapPoints(scatterEvents, smoothingOptions),
    [scatterEvents, smoothingOptions]
  );

  const heatmapValueLabel = isMetersGainedMetric ? "Total Meters Gained" : "Occurrences";

  const scatterPoints: ScatterChartPoint[] = useMemo(() => {
    return scatterEvents.map((event) => {
      const metersLine = isMetersGainedMetric ? `<br>Meters Gained: ${event.meters_gained}m` : "";
      const setPieceResultLine = isSetPieceResultMetric
        ? `<br>Set Piece ${event.set_piece_won === 1 ? "Won" : "Lost"}`
        : "";

      const baseSize = isSetPieceResultMetric ? 28 : 15;

      const point: ScatterChartPoint = {
        x: event.x,
        y: event.y,
        size:
          isMetersGainedMetric && event.meters_gained >= 1 ? event.meters_gained + 15 : baseSize,
        color: event.color,
        label: `Pitch Location: ${event.x}m<br>Action: ${event.action}${metersLine}${setPieceResultLine}<br><i>Click to Watch Clip</i>`,
        eventId: event.event_id,
      };

      if (isSetPieceResultMetric) {
        point.symbol = event.set_piece_won === 1 ? RADIO_BUTTON_CHECKED_SYMBOL : CLOSE_SYMBOL;
        point.symbolRotate = undefined;
      }

      return point;
    });
  }, [isMetersGainedMetric, isSetPieceResultMetric, scatterEvents]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: borderRadius.lg,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: shadows.sm,
        height: "100%",
      }}
    >
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        Location Explorer
      </Typography>
      <Stack spacing={2}>
        <Box sx={{ width: "100%", minHeight: 320 }}>
          <EChartsMetersGainedScatter
            scatterPoints={scatterPoints}
            mode={selectedVisualizationMode === VISUALIZATION_MODES.heatmap ? "heatmap" : "scatter"}
            heatmapPoints={heatmapPoints}
            heatmapValueLabel={heatmapValueLabel}
          />
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <SingleSelect
            label="Action"
            widthFactor={1.5}
            value={selectedActionId}
            setValue={setSelectedActionId}
            options={availableActionOptions}
            none
            disabled={!availableActionOptions.length}
          />
          <SingleSelect
            label="Metric"
            widthFactor={1.5}
            value={selectedMetricId}
            setValue={setSelectedMetricId}
            options={metricOptions}
          />
          <SingleSelect
            label="Visualization"
            widthFactor={1.5}
            value={selectedVisualizationMode}
            setValue={setSelectedVisualizationMode}
            options={visualizationOptions}
          />
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                checked={showHomeTeam}
                onChange={(event) => setShowHomeTeam(event.target.checked)}
              />
            }
            label={eventsForMatch[0]?.home_team || "Home Team"}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showAwayTeam}
                onChange={(event) => setShowAwayTeam(event.target.checked)}
              />
            }
            label={eventsForMatch[0]?.away_team || "Away Team"}
          />
        </Stack>
      </Stack>
    </Paper>
  );
};
